import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import useResizeObserver from 'use-resize-observer'
import base64url from 'base64url'
import {
  Copy,
  Edit,
  Share2,
  Trash,
} from 'react-feather'

import { useAnimationFrame, useGoogleAppdata } from 'hooks'
import { useTimerStore, useSettingsStore } from 'stores'

import {
  Main,
  Confetti,
  Button,
  Modal,
  useModal,
  TextField,
} from 'components'

import {
  Name,
  Counters,
  Number,
  Label,
  Info,
  Buttons,
} from './timerStyle'

dayjs.extend(localizedFormat)

const repeats = {
  'day': 'daily',
  'week': 'weekly',
  'month': 'monthly',
  'year': 'yearly',
}

const Timer = () => {
  const showSeconds = useSettingsStore(state => state.showSeconds)
  const showConfetti = useSettingsStore(state => state.showConfetti)
  const dateFormat = useSettingsStore(state => state.dateFormat)
  const editTimer = useTimerStore(state => state.editTimer)
  const removeTimer = useTimerStore(state => state.removeTimer)
  const timers = useTimerStore(state => state.timers)
  const { id } = useParams()
  const navigate = useNavigate()
  const { save } = useGoogleAppdata()

  const [timer, setTimer] = useState()
  const [counters, setCounters] = useState([])
  const [shareLink, setShareLink] = useState('')
  const [copied, setCopied] = useState(false)
  const shareButtonRef = useRef()
  const { modalProps, modalButtonProps } = useModal('share_modal')

  useEffect(() => {
    const t = timers.find(t => t.id === parseInt(id))
    setTimer({
      ...t,
      date: dayjs(t.date),
    })
  }, [id, timers])

  useEffect(() => {
    if (timer) {
      setShareLink(`${window.location.origin}/import/${base64url(JSON.stringify({
        name: timer.name,
        date: timer.date,
        color: timer.color,
        isRepeating: timer.isRepeating,
        repeatFrequency: timer.repeatFrequency,
      }))}`)
    }
  }, [timer])

  useAnimationFrame(() => {
    if (!timer) return

    let now = dayjs()
    const years = timer.date.diff(now, 'year')
    now = now.add(years, 'year')
    const days = timer.date.diff(now, 'day')
    now = now.add(days, 'day')
    const hours = timer.date.diff(now, 'hour')
    now = now.add(hours, 'hour')
    const minutes = timer.date.diff(now, 'minute')
    now = now.add(minutes, 'minute')
    const seconds = timer.date.diff(now, 'second')

    setCounters([
      years > 0 && {
        label: 'years',
        number: years,
      },
      (years > 0 || days > 0) && {
        label: 'days',
        number: days,
      },
      (years > 0 || days > 0 || hours > 0) && {
        label: 'hours',
        number: hours,
      },
      (years > 0 || days > 0 || hours > 0 || minutes > 0 || !showSeconds) && {
        label: 'minutes',
        number: timer.date.diff(dayjs(), 'second') <= 0 ? 0 : minutes,
      },
      showSeconds && {
        label: 'seconds',
        number: timer.date.diff(dayjs(), 'second') <= 0 ? 0 : seconds,
      },
    ].filter(Boolean))

    if (timer.isRepeating && timer.date.diff(dayjs(), 'seconds') < 1) {
      editTimer(timer.id, {
        date: timer.date.add(1, timer.repeatFrequency).format('YYYY-MM-DDTHH:mm'),
      })
    }
  })

  const {
    ref: wrapperRef,
    height = 700,
    width = 1024,
  } = useResizeObserver({
    box: 'border-box',
  })
  
  return (
    <Main ref={wrapperRef} data-color={timer?.color} style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}>
      <Name>{timer?.name}</Name>

      <Counters>
        {counters.map(counter => (
          <div key={counter.label}>
            <Number>{counter.number}</Number>
            <Label>{counter.label}</Label>
          </div>
        ))}
      </Counters>

      <Info>Countdown until: {timer?.date.format(dateFormat)}</Info>
      {timer?.isRepeating && <Info>Repeats <strong>{repeats[timer?.repeatFrequency]}</strong></Info>}
      {timer?.date.diff(dayjs(), 'second') < 0 && <Info><strong>This countdown has finished</strong></Info>}

      <div style={{ flex: 1 }} />

      <Buttons>
        <Button
          secondary
          onClick={() => {
            if (window.confirm('Are you sure you want to delete this countdown?')) {
              removeTimer(parseInt(id))
              save()
              navigate('/')
            }
          }}
          color={timer?.color}
        >
          <Trash size="1.2em" />
          <span>Delete</span>
        </Button>

        <div />

        <Button
          secondary
          color={timer?.color}
          {...modalButtonProps}
        >
          <Share2 size="1.2em" />
          <span>Share</span>
        </Button>
        <Button
          as={Link}
          to={`/timer/${id}/edit`}
          color={timer?.color}
        >
          <Edit size="1.2em" />
          <span>Edit</span>
        </Button>
      </Buttons>

      {timer?.date.diff(dayjs(), 'second') < 0 && showConfetti && <Confetti height={height} width={width} />}

      <Modal
        {...modalProps}
        focusRef={shareButtonRef}
        aria-label="Share this countdown"
      >
        <TextField
          label="Share this link to let others import this countdown"
          value={shareLink}
          readOnly
          id="share_link"
        />
        <Buttons style={{ marginTop: 0 }}>
          {copied && <div>Copied to your clipboard!</div>}

          <Button ref={shareButtonRef} onClick={() => {
            if (!navigator.clipboard) return
            navigator.clipboard.writeText(shareLink).then(() => {
              setCopied(true)
              window.setTimeout(() => setCopied(false), 2000)
            })
          }}>
            <Copy size="1.2em" />
            <span>Copy link</span>
          </Button>
        </Buttons>
      </Modal>
    </Main>
  )
}

export default Timer